/**
 * Milestone utility functions for performance optimization and data manipulation
 */

import { CombinedMilestone, CombinedMilestoneTask } from "../types";

export const getMilestoneId = (milestone: CombinedMilestone): string => {
     if (milestone.milestoneName) return milestone.milestoneName;
     const taskWithId = milestone.tasks.find(task => task.divisionTemplate?.id);
     if (taskWithId?.divisionTemplate?.id) return taskWithId.divisionTemplate.id;
     return `milestone-${milestone.milestoneOrder}`;
};

export const getMilestoneOrder = (milestone: CombinedMilestone): number => {
     if (typeof milestone.milestoneOrder === 'number') return milestone.milestoneOrder;
     const taskWithOrder = milestone.tasks.find(task =>
          typeof task.divisionTemplate?.mileStoneOrder === 'number'
     );
     return taskWithOrder?.divisionTemplate?.mileStoneOrder || 0;
};

export const milestoneHasTasks = (milestone: CombinedMilestone): boolean => {
     return Array.isArray(milestone.tasks) && milestone.tasks.length > 0;
};

export const getFirstValidTask = (milestone: CombinedMilestone) => {
     return milestone.tasks.find(task =>
          task.divisionTemplate && typeof task.divisionTemplate === 'object'
     );
};

export const createAutomatedTaskNameSet = (automatedTasks: { taskName: string }[]): Set<string> => {
     return new Set(automatedTasks.map(task => task.taskName));
};

export const isEqual = (a: any, b: any): boolean => {
     if (a === b) return true;
     if (a == null || b == null) return a === b;
     if (typeof a !== typeof b) return false;

     try {
          return JSON.stringify(a) === JSON.stringify(b);
     } catch {
          return false;
     }
};

const getTaskStateInternal = (task: CombinedMilestoneTask) => {
     return {
          milestoneTaskStatus: task.milestoneTaskData?.status ?? null,
          completionDate: task.milestoneTaskData?.completionDate ?? null,
          dueDate: task.milestoneTaskData?.dueDate ?? null,
          taskName: task.milestoneTaskData?.taskName ?? task.divisionTemplate?.taskName,
          automated: task.divisionTemplate?.automated === true,
          divisionStatus: task.divisionTemplate?.status
     };
};

export const getTaskCheckboxState = (task: CombinedMilestoneTask): boolean => {
     if (!task) return false;
     const { completionDate, automated, milestoneTaskStatus } = getTaskStateInternal(task);
     if (automated) return true;
     if (completionDate != null && milestoneTaskStatus) return true;
     return false;
};

export const isTaskStrikeThrough = (task: CombinedMilestoneTask): boolean => {
     if (!task) return false;
     const { milestoneTaskStatus, completionDate, automated } = getTaskStateInternal(task);
     if (automated) return false;
     if (milestoneTaskStatus === false && completionDate !== null) {
          return true;
     }
     return false;
};

export const isTaskIndeterminate = (task: CombinedMilestoneTask): boolean => {
     if (!task) return false;
     const { milestoneTaskStatus, completionDate, divisionStatus, automated } = getTaskStateInternal(task);
     if (automated) return false;
     // Indeterminate if task has division status false
     if (!divisionStatus && !milestoneTaskStatus) return true;

     // Indeterminate if task has status false and completion date is set (not applicable)
     if (milestoneTaskStatus === false && completionDate !== null) {
          return true;
     }
     return false;
};


export const shouldShowTaskMenu = (task: CombinedMilestoneTask): boolean => {
     if (!task) return false;
     const { automated, divisionStatus } = getTaskStateInternal(task);
     if (automated) return false;
     return divisionStatus;
};

export const isTaskDisabled = (task: CombinedMilestoneTask): boolean => {
     if (!task) return true;
     const { automated, divisionStatus } = getTaskStateInternal(task);
     if (automated) return true;
     return !divisionStatus;
};

const getMilestoneState = (milestone: CombinedMilestone) => {
     // Filter out automated tasks and tasks with divisionStatus=false
     const eligibleTasks = milestone.tasks.filter(
          (task) =>
               task.divisionTemplate?.automated !== true &&
               task.divisionTemplate?.status !== false
     );

     // For checkbox state, consider all non-automated tasks regardless of division status
     const allNonAutomatedTasks = milestone.tasks.filter(
          (task) => task.divisionTemplate?.automated !== true
     );

     let checkedCount = 0;
     let notApplicableCount = 0;
     let disabledCount = 0;
     let allTasksCheckedCount = 0;

     for (const task of eligibleTasks) {
          if (getTaskCheckboxState(task)) checkedCount++;
          if (isMilestoneNotApplicable({ tasks: [task] } as CombinedMilestone)) notApplicableCount++;
          if (isTaskDisabled(task)) disabledCount++;
     }

     // Count completed tasks among all non-automated tasks
     for (const task of allNonAutomatedTasks) {
          if (getTaskCheckboxState(task)) allTasksCheckedCount++;
     }

     return {
          eligibleTasks,
          allNonAutomatedTasks,
          checkedCount,
          notApplicableCount,
          disabledCount,
          allTasksCheckedCount
     };
};

export const getMilestoneCheckboxState = (milestone: CombinedMilestone): boolean => {
     const { allNonAutomatedTasks, allTasksCheckedCount } = getMilestoneState(milestone);
     // Check milestone if all non-automated tasks are completed, regardless of division status
     return allNonAutomatedTasks.length > 0 && allTasksCheckedCount === allNonAutomatedTasks.length;
};

export const isMilestoneIndeterminate = (milestone: CombinedMilestone): boolean => {
     const { allNonAutomatedTasks, eligibleTasks, notApplicableCount } = getMilestoneState(milestone);
     if (allNonAutomatedTasks.length === 0) return false;

     // Check if at least one task has status false
     const hasTaskWithStatusFalse = allNonAutomatedTasks.some(task =>
          task.milestoneTaskData?.status === false
     );

     // Check if any task has null/undefined status and division status is false
     const hasTaskWithNullStatusAndDisabledDivision = allNonAutomatedTasks.some(task => {
          const taskStatus = task.milestoneTaskData?.status;
          const divisionStatus = task.divisionTemplate?.status;
          const isNullStatus = taskStatus === null || taskStatus === undefined;
          const isDisabledDivision = divisionStatus === false;
          return isNullStatus && isDisabledDivision;
     });

     // Indeterminate if some but not all non-automated tasks are completed
     const completedTasks = allNonAutomatedTasks.filter(task => getTaskCheckboxState(task));
     const hasCompletedTasks = completedTasks.length > 0;
     const allCompleted = completedTasks.length === allNonAutomatedTasks.length;

     // Also indeterminate if all eligible tasks are marked as not applicable
     const allNotApplicable = eligibleTasks.length > 0 && notApplicableCount === eligibleTasks.length;

     const result = hasTaskWithStatusFalse || hasTaskWithNullStatusAndDisabledDivision || (hasCompletedTasks && !allCompleted) || allNotApplicable;

     return result;
};

export const isMilestoneStrikeThrough = (milestone: CombinedMilestone): boolean => {
     const { eligibleTasks, notApplicableCount } = getMilestoneState(milestone);
     return eligibleTasks.length > 0 && notApplicableCount === eligibleTasks.length;
};

export const isMilestoneDisabled = (milestone: CombinedMilestone): boolean => {
     if (!milestone || !milestone.tasks || milestone.tasks.length === 0) {
          return true;
     }

     // Check if all tasks have divisionStatus=false
     const allTasksDisabled = milestone.tasks.every((task) => task.divisionTemplate?.status === false);

     // Also check if there are no eligible tasks (automated tasks excluded)
     const eligibleTasks = milestone.tasks.filter(
          (task) =>
               task.divisionTemplate?.automated !== true &&
               task.divisionTemplate?.status !== false
     );

     return allTasksDisabled || eligibleTasks.length === 0;
};

export const isMilestoneNotApplicable = (milestone: CombinedMilestone): boolean => {
     if (!milestone || !milestone.tasks || milestone.tasks.length === 0) {
          return false;
     }

     // Filter out automated tasks and tasks with divisionStatus=false
     const eligibleTasks = milestone.tasks.filter(
          (task) =>
               task.divisionTemplate?.automated !== true &&
               task.divisionTemplate?.status !== false
     );

     if (eligibleTasks.length === 0) {
          return false;
     }

     // Check if all eligible tasks are marked as not applicable
     const notApplicableTasks = eligibleTasks.filter((task) =>
          task.milestoneTaskData?.status === false &&
          task.milestoneTaskData?.completionDate !== null
     );

     // Return true if all eligible tasks are marked as not applicable
     return notApplicableTasks.length === eligibleTasks.length && eligibleTasks.length > 0;
};

export const areAllTasksDisabled = (milestone: CombinedMilestone): boolean => {
     if (!milestone || !milestone.tasks || milestone.tasks.length === 0) {
          return true;
     }

     // Check if all tasks have divisionStatus=false
     const allTasksDisabled = milestone.tasks.every((task) => task.divisionTemplate?.status === false);

     // Also check if there are no eligible tasks (automated tasks excluded)
     const eligibleTasks = milestone.tasks.filter(
          (task) =>
               task.divisionTemplate?.automated !== true &&
               task.divisionTemplate?.status !== false
     );

     return allTasksDisabled || eligibleTasks.length === 0;
};

